import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { CalendarPlus, Clock, Car, Phone, X } from 'lucide-react';
import { useVertical } from '../../../../context/VerticalContext';
import { APPOINTMENT_TYPES } from '../../../../components/dashboard/appointmentTypes';

export interface DayAppointment {
    id: string;
    start_time: string;
    end_time: string;
    customer_name: string;
    customer_phone: string;
    vehicle_vrm: string;
    vehicle_make: string;
    vehicle_model: string;
    status: 'scheduled' | 'completed' | 'cancelled' | 'no_show';
    appointment_type: string | null;
    notes: string;
    linked_job_id: string | null;
    linked_quote_id: string | null;
}

/**
 * Everything booked on one day, readable.
 *
 * A month cell is about 100px tall and was trying to show time, customer and
 * status for every appointment on the day — which worked for one and fell apart
 * at three. The grid now shows a count and defers the detail to here, where
 * there's room to show it properly on a phone.
 */
export const DayDetailModal = ({
    day,
    appointments,
    onClose,
    onSelect,
    onBook,
}: {
    day: Date | null;
    appointments: DayAppointment[];
    onClose: () => void;
    onSelect: (appt: DayAppointment) => void;
    onBook: (day: Date) => void;
}) => {
    const { molded } = useVertical();
    if (!day) return null;

    const statusStyle = (status: DayAppointment['status']) => {
        switch (status) {
            case 'completed':
                return 'bg-success/10 border-success/40 text-success';
            case 'cancelled':
            case 'no_show':
                return 'bg-danger/10 border-danger/40 text-danger';
            default:
                return 'bg-gold-fill/10 border-gold/40 text-gold';
        }
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-scrim/60 backdrop-blur-sm
                       px-0 sm:px-4 pt-[max(1rem,env(safe-area-inset-top))]"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
            aria-label={`Appointments on ${format(day, 'EEEE d MMMM')}`}
        >
            <div className="w-full sm:max-w-lg bg-panel border border-line rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
                <header className="flex items-start justify-between gap-3 p-5 border-b border-line shrink-0">
                    <div>
                        <h3 className="text-lg font-bold font-display text-fg">
                            {format(day, 'EEEE d MMMM')}
                        </h3>
                        <p className="text-[13px] text-fg-3 mt-0.5">
                            {appointments.length === 0
                                ? 'Nothing booked'
                                : `${appointments.length} appointment${appointments.length === 1 ? '' : 's'}`}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-fg-3 hover:text-fg hover:bg-card transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
                    {appointments.length === 0 && (
                        <p className="text-center text-fg-3 py-8 text-sm">
                            No appointments on this day.
                        </p>
                    )}

                    {appointments.map((appt) => (
                        <button
                            key={appt.id}
                            onClick={() => onSelect(appt)}
                            className="w-full text-left p-3.5 rounded-xl bg-card border border-line hover:border-gold/40 transition-colors"
                        >
                            <div className="flex items-center justify-between gap-3 mb-2">
                                <span className="flex items-center gap-1.5 font-mono tabular-nums text-[15px] font-semibold text-fg">
                                    <Clock className="w-3.5 h-3.5 text-fg-4" />
                                    {format(new Date(appt.start_time), 'HH:mm')}
                                    {appt.end_time && ` – ${format(new Date(appt.end_time), 'HH:mm')}`}
                                </span>
                                <span className="flex items-center gap-1.5 shrink-0">
                                    {/* The reason for the visit, the same label the
                                        dashboard appointments card shows. Time,
                                        person and vehicle told you WHO was coming;
                                        this is the half that says what for. */}
                                    {appt.appointment_type && (
                                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border border-gold/35 bg-gold-fill/10 text-gold">
                                            {APPOINTMENT_TYPES.find((t) => t.value === appt.appointment_type)?.label ?? appt.appointment_type}
                                        </span>
                                    )}
                                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${statusStyle(appt.status)}`}>
                                        {appt.status === 'no_show' ? 'no show' : appt.status}
                                    </span>
                                </span>
                            </div>

                            <p className="text-[15px] font-semibold text-fg truncate">
                                {appt.customer_name || 'Unnamed'}
                            </p>

                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[13px] text-fg-3">
                                {appt.vehicle_vrm && (
                                    <span className="flex items-center gap-1.5">
                                        <Car className="w-3.5 h-3.5" />
                                        <span className="font-mono font-semibold text-fg-2">{appt.vehicle_vrm}</span>
                                        {appt.vehicle_make && <span>{appt.vehicle_make} {appt.vehicle_model}</span>}
                                    </span>
                                )}
                                {appt.customer_phone && (
                                    <span className="flex items-center gap-1.5">
                                        <Phone className="w-3.5 h-3.5" />
                                        {appt.customer_phone}
                                    </span>
                                )}
                            </div>

                            {appt.notes && (
                                <p className="text-[13px] text-fg-3 mt-2 line-clamp-2">{appt.notes}</p>
                            )}
                        </button>
                    ))}
                </div>

                <footer className="p-4 border-t border-line shrink-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
                    <button
                        onClick={() => onBook(day)}
                        className="w-full flex items-center justify-center gap-2 h-12 rounded-lg font-bold fd-btn-primary transition-all active:scale-[0.98]"
                    >
                        <CalendarPlus className="w-4 h-4" />
                        Book {molded.jargon.client_label.toLowerCase()} in
                    </button>
                </footer>
            </div>
        </div>,
        document.body
    );
};
